"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * /guides/glossary — the public Guide Center Glossary (Budget Model v2,
 * ADR-052 §12; consumer revision 2026-09-04). Renders THE canonical glossary
 * module from @bitebalance/i18n: categories in their stable order, each
 * populated category's terms as compact entries. Information hierarchy:
 *
 *   collapsed (default)  name · kind badge · the one-sentence definition
 *   expanded             + the short explanation (+ "For example …")
 *
 * The expand control is a real button (aria-expanded / aria-controls,
 * "Show more" / "Show less"). Scaffolded categories show the "coming soon"
 * state and never an invented definition. Term ids are the deep-link
 * contract only — they live in URLs and DOM ids and are never rendered as
 * text.
 *
 * Deep links are the URL contract for every ⓘ "Learn more":
 *   /guides/glossary#<term-id>       scrolls to, focuses, highlights AND expands
 *   /guides/glossary?term=<term-id>  accepted and normalized to the hash
 * An unknown id falls back to the top of the page — never an error.
 *
 * Same standalone public pattern as GuideHubPage / SaveYourDayPage: no
 * auth, no personal context, brand header + language toggle + skip link.
 */

import { useEffect, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  GLOSSARY_CATEGORIES,
  glossaryTermsInCategory,
  isGlossaryTermId,
  type GlossaryTermId,
} from "@bitebalance/i18n"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { BrandMarkV2 } from "@/components/landing/BrandMarkV2"
import { PageEntrance } from "@/components/PageEntrance"
import { useI18n } from "@/i18n"
import { BUDGET_GUIDE_PATH } from "@/lib/budgetGuide"
import { glossaryKindLabel, glossaryUi } from "@/lib/glossary"

// WS6 (audit R-16): only categories that carry terms are published — a scaffolded
// category is never shown as "Coming soon" to a user (prove or remove).
const PUBLISHED_CATEGORIES = GLOSSARY_CATEGORIES.filter(category => category.populated)

export default function GlossaryPage() {
  const { t, locale } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const ui = glossaryUi(locale)
  const [activeTerm, setActiveTerm] = useState<GlossaryTermId | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<GlossaryTermId>>(() => new Set())

  useEffect(() => {
    const previousTitle = document.title
    document.title = `${ui.title} — BiteBalance`
    return () => {
      document.title = previousTitle
    }
  }, [ui.title])

  // `?term=<id>` is the mobile-style fallback form; normalize it to the hash
  // so there is exactly one canonical URL per entry.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const term = params.get("term")
    if (term === null) return
    params.delete("term")
    const search = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: isGlossaryTermId(term) ? `#${term}` : "",
      },
      { replace: true }
    )
  }, [location.search, location.pathname, navigate])

  // Resolve the hash to an entry: expand, scroll, focus (tabIndex=-1) and
  // highlight. Unknown ids fall back to the top — quietly.
  useEffect(() => {
    const id = location.hash.replace(/^#/, "")
    if (id && isGlossaryTermId(id)) {
      setActiveTerm(id)
      setExpanded(prev => (prev.has(id) ? prev : new Set(prev).add(id)))
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView?.({ block: "start" })
        el.focus({ preventScroll: true })
      }
      return
    }
    setActiveTerm(null)
    if (id) window.scrollTo?.({ top: 0 })
  }, [location.hash])

  function toggle(id: GlossaryTermId) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <PageEntrance>
      <div className="min-h-screen bg-cream text-ink">
        <a
          href="#main"
          className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
        >
          {t("nav.skipToContent")}
        </a>

        <header className="border-b border-line bg-paper">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
            <Link
              to="/guides"
              className="flex items-center gap-2 text-sm font-semibold text-ink-2 hover:text-ink"
            >
              <BrandMarkV2 size={22} />
              <span>← {t("guides.hub.eyebrow")}</span>
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <main id="main">
          <div className="mx-auto max-w-3xl px-4 py-14 md:px-8 md:py-20">
            <p className="text-sm font-semibold tracking-wide text-green uppercase">
              {t("guides.hub.eyebrow")}
            </p>
            <h1 className="mt-3 text-4xl leading-tight font-black text-ink md:text-5xl">
              {ui.title}
            </h1>
            <p className="mt-5 max-w-prose text-lg text-ink-2">{ui.subtitle}</p>

            <nav aria-label={ui.title} className="mt-8 flex flex-wrap gap-2">
              {PUBLISHED_CATEGORIES.map(category => (
                <a
                  key={category.id}
                  href={`#category-${category.id}`}
                  className="rounded-bb-full border border-line bg-paper px-3 py-1.5 text-sm font-medium text-ink-2 hover:border-green/60 hover:text-ink"
                >
                  {category.title[locale]}
                </a>
              ))}
            </nav>

            <div className="mt-12 flex flex-col gap-12">
              {PUBLISHED_CATEGORIES.map(category => {
                const terms = glossaryTermsInCategory(category.id)
                return (
                  <section
                    key={category.id}
                    id={`category-${category.id}`}
                    aria-labelledby={`category-${category.id}-title`}
                    className="flex flex-col gap-4"
                  >
                    <h2
                      id={`category-${category.id}-title`}
                      className="text-2xl leading-snug font-bold text-ink md:text-3xl"
                    >
                      {category.title[locale]}
                    </h2>

                    {/* Definitions live here; the connected explanation is
                        the Budget Guide (Glossary = definitions, Guide =
                        explanations). */}
                    {category.id === "budget-shopping" && (
                      <p className="text-sm text-ink-2">
                        {t("guides.hub.eyebrow")}:{" "}
                        <Link
                          to={BUDGET_GUIDE_PATH}
                          data-testid="glossary-budget-guide-link"
                          className="font-semibold text-green underline underline-offset-2"
                        >
                          {t("views.budget.budgetGuideLinkText")}
                        </Link>
                      </p>
                    )}

                    {!category.populated || terms.length === 0 ? (
                      <p
                        data-testid={`glossary-coming-soon-${category.id}`}
                        className="rounded-bb-lg border border-dashed border-line bg-paper p-5 text-sm text-ink-2"
                      >
                        {ui.comingSoon}
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {terms.map(term => {
                          const text = term.text[locale]
                          const active = activeTerm === term.id
                          const open = expanded.has(term.id)
                          const detailsId = `${term.id}-details`
                          return (
                            <article
                              key={term.id}
                              id={term.id}
                              tabIndex={-1}
                              data-active={active ? "true" : undefined}
                              data-expanded={open ? "true" : "false"}
                              aria-labelledby={`${term.id}-name`}
                              className={
                                "scroll-mt-24 rounded-bb-xl border bg-paper p-5 outline-none transition-colors md:p-6 " +
                                (active ? "border-green shadow-[var(--bb-shadow)]" : "border-line")
                              }
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 id={`${term.id}-name`} className="text-lg font-bold text-ink">
                                  {text.name}
                                </h3>
                                <span className="rounded-bb-full border border-line bg-cream px-2 py-0.5 text-xs font-semibold text-ink-2">
                                  {glossaryKindLabel(term.kind, locale)}
                                </span>
                              </div>
                              <p className="mt-2 text-base text-ink">{text.shortDefinition}</p>

                              {open && (
                                <div id={detailsId} className="mt-3 flex flex-col gap-3">
                                  <p className="text-base leading-relaxed text-ink-2">{text.explanation}</p>
                                  {text.example && (
                                    <p className="text-sm leading-relaxed text-ink-2">
                                      <span className="font-semibold text-ink">{ui.example}: </span>
                                      {text.example}
                                    </p>
                                  )}
                                </div>
                              )}

                              <button
                                type="button"
                                aria-expanded={open}
                                aria-controls={detailsId}
                                onClick={() => toggle(term.id)}
                                className="mt-3 min-h-9 text-sm font-semibold text-green underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-green"
                              >
                                {open ? ui.showLess : ui.showMore}
                              </button>
                            </article>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )
              })}
            </div>
          </div>
        </main>
      </div>
    </PageEntrance>
  )
}
